import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import type { VerifyEInvoiceCredentialRequest } from '@/lib/api/einvoice-credential-api'
import { useAuth } from '@/hooks/use-auth'
import { einvoiceCredentialApi } from '@/lib/api/einvoice-credential-api'
import { m } from '@/lib/paraglide/messages'
import { ApiError } from '@/lib/api'

export const einvoiceCredentialKeys = {
    all: ['einvoice-credential'] as const,
    mine: (userID: string | undefined, actorID?: string) =>
        [
            ...einvoiceCredentialKeys.all,
            actorID ?? 'self',
            userID ?? 'anonymous',
        ] as const,
}

export function useEInvoiceCredential() {
    const auth = useAuth()
    const {
        user,
        impersonating,
        isImpersonating,
        hasPermission,
        arePermissionsLoaded,
    } = auth
    return {
        ...useQuery({
            queryKey: einvoiceCredentialKeys.mine(
                user?.id,
                impersonating?.actor.id,
            ),
            queryFn: async () =>
                (await einvoiceCredentialApi.get()).data ?? null,
            enabled: arePermissionsLoaded && hasPermission('invoices.export'),
            staleTime: 30_000,
        }),
        isImpersonating,
    }
}

export function useVerifyEInvoiceCredential() {
    const queryClient = useQueryClient()
    const { user, impersonating } = useAuth()
    const pending = useRef<VerifyEInvoiceCredentialRequest | null>(null)
    const mutation = useMutation({
        mutationFn: async () =>
            (await einvoiceCredentialApi.verify(pending.current!)).data ?? null,
        onSuccess: (data) => {
            queryClient.setQueryData(
                einvoiceCredentialKeys.mine(user?.id, impersonating?.actor.id),
                data,
            )
            toast.success(
                data?.misa_username
                    ? m.einvoice_credential_connected({
                          userName: data.misa_username,
                      })
                    : m.einvoice_credential_title(),
            )
        },
    })
    return {
        ...mutation,
        mutateAsync: async (data: VerifyEInvoiceCredentialRequest) => {
            pending.current = data
            try {
                return await mutation.mutateAsync(undefined)
            } finally {
                pending.current = null
                mutation.reset()
            }
        },
    }
}

export function useRemoveEInvoiceCredential() {
    const queryClient = useQueryClient()
    const { user, impersonating } = useAuth()
    return useMutation({
        mutationFn: () => einvoiceCredentialApi.remove(),
        onSuccess: () => {
            queryClient.setQueryData(
                einvoiceCredentialKeys.mine(user?.id, impersonating?.actor.id),
                {
                    configured: false,
                    misa_username: '',
                    misa_user_id: '',
                    invalid_since: null,
                    invalid_reason: '',
                },
            )
            toast.success(m.einvoice_credential_removed())
        },
    })
}

export function invalidateRejectedEInvoiceCredential(
    queryClient: ReturnType<typeof useQueryClient>,
    error: unknown,
) {
    if (
        error instanceof ApiError &&
        ['EINVOICE_CREDENTIAL_MISSING', 'EINVOICE_CREDENTIAL_INVALID'].includes(
            error.code,
        )
    ) {
        void queryClient.invalidateQueries({
            queryKey: einvoiceCredentialKeys.all,
        })
    }
}
